/**
 * Reading what an NX action or survey sent back (server 064/065), so the
 * screen can show a person a table instead of JSON. Pure functions, tested in
 * test/nx_results.test.ts against the shapes the first real server returned
 * (HD Witness 6.1.0.42176, 2026-09-29).
 *
 * What the first diagnosis taught: most of NX's "offline" cameras were a
 * second entry for a camera that was recording under another entry at the
 * same address — added once natively and once over ONVIF. So every offline
 * camera is checked for a working twin, and one is called a duplicate rather
 * than a fault.
 */

export interface NxDevice {
  id: string;
  name: string;
  ip: string | null;
  vendor: string | null;
  model: string | null;
  status: string | null;
}

/** NX writes ids as `{uuid}` in some answers and bare in others. */
export const nxId = (id: string): string => id.replace(/[{}]/g, '').toLowerCase();

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

function body(result: Record<string, unknown>, key: string): unknown {
  const answers = result['answers'];
  if (!isObject(answers)) return undefined;
  const a = answers[key];
  return isObject(a) ? a['body'] : undefined;
}

function answerKeys(result: Record<string, unknown>): string[] {
  const answers = result['answers'];
  return isObject(answers) ? Object.keys(answers) : [];
}

const ipOf = (url: unknown): string | null => {
  const m = typeof url === 'string' ? /\/\/([^/:]+)/.exec(url) : null;
  return m?.[1] ?? null;
};

/**
 * The cameras NX knows: from `_devices` (agent 0.9.2 adds it to every action)
 * or, in a survey, from its /rest/v4 devices answer.
 */
export function devicesOf(result: Record<string, unknown>): Map<string, NxDevice> {
  const out = new Map<string, NxDevice>();
  const listed = Array.isArray(result['_devices']) ? result['_devices'] : body(result, '/rest/v4/devices');
  if (!Array.isArray(listed)) return out;
  for (const d of listed) {
    if (!isObject(d) || typeof d['id'] !== 'string') continue;
    const id = nxId(d['id']);
    out.set(id, {
      id,
      name: str(d['name']) ?? id,
      ip: str(d['ip']) ?? ipOf(d['url']),
      vendor: str(d['vendor']),
      model: str(d['model']),
      status: str(d['status']),
    });
  }
  return out;
}

// ── Diagnose offline cameras ────────────────────────────────────────────────

export type Cause = 'wrong_password' | 'camera_fault' | 'unreachable' | 'unknown';

export const CAUSE_WORDS: Record<Cause, string> = {
  wrong_password: 'Wrong password',
  camera_fault: 'Camera rejected NX’s request',
  unreachable: 'Not answering',
  unknown: 'Unclear',
};

/** The same, mid-sentence. */
const CAUSE_COUNTED: Record<Cause, string> = {
  wrong_password: 'wrong password',
  camera_fault: 'rejected NX’s request',
  unreachable: 'not answering',
  unknown: 'unclear',
};

/** NX's `init` sentence, read for its cause. The texts are NX's own, seen on 6.1. */
export function causeOf(init: string | null): Cause {
  if (init === null) return 'unknown';
  if (/not authori[sz]ed/i.test(init)) return 'wrong_password';
  if (/HTTP error 0\b|timed? ?out|no route|connection refused|unreachable|host is down/i.test(init)) return 'unreachable';
  if (/failed with error/i.test(init)) return 'camera_fault';
  return 'unknown';
}

export interface DiagnosisRow {
  id: string;
  name: string;
  ip: string | null;
  model: string | null;
  vendor: string | null;
  cause: Cause;
  /** NX's first sentence, without its boilerplate advice. */
  detail: string;
  /** A working entry at the same address: this one is a stale duplicate, not a fault. */
  duplicateOf: { name: string; status: string } | null;
}

const WORKING = new Set(['Online', 'Recording']);

export function diagnosisRows(result: Record<string, unknown>): DiagnosisRow[] {
  const devices = devicesOf(result);
  const rows: DiagnosisRow[] = [];
  for (const key of answerKeys(result)) {
    const m = /^\/rest\/v\d+\/devices\/([^/]+)\/status$/.exec(key);
    if (m === null) continue;
    const b = body(result, key);
    const id = nxId(m[1] ?? '');
    const dev = devices.get(id);
    const init = isObject(b) ? str(b['init']) : null;
    const twin =
      dev?.ip == null
        ? undefined
        : [...devices.values()].find((d) => d.id !== id && d.ip === dev.ip && WORKING.has(d.status ?? ''));
    rows.push({
      id,
      name: dev?.name ?? id,
      ip: dev?.ip ?? null,
      model: dev?.model ?? null,
      vendor: dev?.vendor ?? null,
      cause: causeOf(init),
      detail: (init ?? 'NX gave no reason').split('\n')[0]?.trim() ?? '',
      duplicateOf: twin === undefined ? null : { name: twin.name, status: twin.status ?? '' },
    });
  }
  // Real faults first, duplicates last; then by address, so a site reads in order.
  const rank = (r: DiagnosisRow): number => (r.duplicateOf !== null ? 9 : { unreachable: 0, camera_fault: 1, wrong_password: 2, unknown: 3 }[r.cause]);
  const ipKey = (ip: string | null): string => (ip ?? '~').split('.').map((p) => p.padStart(3, '0')).join('.');
  return rows.sort((a, b) => rank(a) - rank(b) || ipKey(a.ip).localeCompare(ipKey(b.ip)));
}

export function diagnosisSummary(rows: DiagnosisRow[]): string {
  if (rows.length === 0) return 'No camera is offline.';
  const dup = rows.filter((r) => r.duplicateOf !== null).length;
  const rest = rows.filter((r) => r.duplicateOf === null);
  const parts: string[] = [];
  if (dup > 0) parts.push(`${dup} at the same address as a working camera`);
  for (const cause of ['unreachable', 'camera_fault', 'wrong_password', 'unknown'] as const) {
    const n = rest.filter((r) => r.cause === cause).length;
    if (n > 0) parts.push(`${n} ${CAUSE_COUNTED[cause]}`);
  }
  return `${rows.length} offline: ${parts.join(', ')}.`;
}

// ── Storage detail ──────────────────────────────────────────────────────────

export interface Disk {
  id: string;
  path: string;
  online: boolean;
  writable: boolean;
  recording: boolean;
  system: boolean;
  backup: boolean;
  totalB: number | null;
  freeB: number | null;
  reservedB: number | null;
  /** Runtime flags other than the ordinary `used`: beingRebuilt, tooSmall, … */
  flags: string[];
}

export function disks(result: Record<string, unknown>): Disk[] {
  const configured = body(result, '/rest/v3/servers/this/storages');
  const status = body(result, '/rest/v3/servers/this/storages/*/status');
  const byId = new Map<string, Record<string, unknown>>();
  if (Array.isArray(status)) for (const s of status) if (isObject(s) && typeof s['storageId'] === 'string') byId.set(nxId(s['storageId']), s);
  const out: Disk[] = [];
  const seen = new Set<string>();
  const add = (id: string, c: Record<string, unknown> | undefined, s: Record<string, unknown> | undefined): void => {
    seen.add(id);
    const flags = `${str(s?.['persistentFlags']) ?? str(isObject(c?.['parameters']) ? (c?.['parameters'] as Record<string, unknown>)['persistentStorageStatusFlags'] : null) ?? ''}`;
    out.push({
      id,
      path: str(s?.['url']) ?? str(c?.['path']) ?? id,
      online: s !== undefined ? s['isOnline'] === true : c?.['status'] === 'Online',
      writable: s?.['isWritable'] === true,
      recording: (s?.['isUsedForWriting'] ?? c?.['isUsedForWriting']) === true,
      system: /\bsystem\b/.test(flags),
      backup: (s?.['isBackup'] ?? c?.['isBackup']) === true,
      totalB: num(s?.['totalSpace']),
      freeB: num(s?.['freeSpace']),
      reservedB: num(s?.['reservedSpace'] ?? c?.['spaceLimitB']),
      flags: (str(s?.['runtimeFlags']) ?? '').split('|').filter((f) => f !== '' && f !== 'used'),
    });
  };
  if (Array.isArray(configured)) {
    for (const c of configured) if (isObject(c) && typeof c['id'] === 'string') add(nxId(c['id']), c, byId.get(nxId(c['id'])));
  }
  for (const [id, s] of byId) if (!seen.has(id)) add(id, undefined, s);
  return out.sort((a, b) => Number(b.recording) - Number(a.recording) || a.path.localeCompare(b.path));
}

export interface Retention {
  id: string;
  name: string;
  days: number;
  bytes: number;
  bitrateBps: number | null;
  /** Share of the archive span actually recorded: 1 is continuous; low means gaps or motion-only. */
  coverage: number | null;
}

export function retention(result: Record<string, unknown>): Retention[] {
  const stats = body(result, '/rest/v3/servers/this/recordingStatistics');
  if (!Array.isArray(stats)) return [];
  const devices = devicesOf(result);
  return stats
    .filter(isObject)
    .map((s) => {
      const id = nxId(String(s['deviceId'] ?? ''));
      const span = num(s['archiveDurationS']) ?? 0;
      const recorded = num(s['recordedS']);
      return {
        id,
        name: devices.get(id)?.name ?? id,
        days: span / 86_400,
        bytes: num(s['recordedBytes']) ?? 0,
        bitrateBps: num(s['averageBitrate']),
        coverage: recorded !== null && span > 0 ? Math.min(1, recorded / span) : null,
      };
    })
    .sort((a, b) => a.days - b.days);
}

// ── Survey ──────────────────────────────────────────────────────────────────

export interface Alarm {
  kind: string;
  name: string;
  level: string;
  text: string;
}

/** NX's own alarms, flattened: `{devices: {<id>: {<group>: {<param>: [{level, text}]}}}}`. */
export function alarms(result: Record<string, unknown>): Alarm[] {
  const b = body(result, '/rest/v4/metrics/alarms') ?? body(result, '/rest/v3/system/metrics/alarms');
  if (!isObject(b)) return [];
  const devices = devicesOf(result);
  // Servers are named from the survey's server list; NX's alarms carry only ids.
  const servers = new Map<string, string>();
  const listed = body(result, '/rest/v4/servers');
  if (Array.isArray(listed)) for (const x of listed) if (isObject(x) && typeof x['id'] === 'string' && typeof x['name'] === 'string') servers.set(nxId(x['id']), x['name']);
  const out: Alarm[] = [];
  for (const [kind, resources] of Object.entries(b)) {
    if (!isObject(resources)) continue;
    for (const [id, groups] of Object.entries(resources)) {
      if (!isObject(groups)) continue;
      for (const params of Object.values(groups)) {
        if (!isObject(params)) continue;
        for (const list of Object.values(params)) {
          if (!Array.isArray(list)) continue;
          for (const a of list) {
            if (!isObject(a)) continue;
            const name = devices.get(nxId(id))?.name ?? servers.get(nxId(id)) ?? id;
            out.push({ kind: kind.replace(/s$/, ''), name, level: str(a['level']) ?? '', text: str(a['text']) ?? '' });
          }
        }
      }
    }
  }
  return out.sort((a, b) => (a.level === 'error' ? 0 : 1) - (b.level === 'error' ? 0 : 1) || a.name.localeCompare(b.name));
}

export function statusCounts(result: Record<string, unknown>): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const d of devicesOf(result).values()) counts.set(d.status ?? 'Unknown', (counts.get(d.status ?? 'Unknown') ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]);
}

export function formatBytes(b: number | null): string {
  if (b === null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = b;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v >= 100 || i === 0 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
}
